import { WandSparkles } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { InfoHint } from '../InfoHint';
import type { CounterfactualProjection, CounterfactualScript, CounterfactualStyle } from '../../lib/relationship-intelligence-engine';

const STYLE_OPTIONS: Array<{ id: CounterfactualStyle; label: string }> = [
  { id: 'soft_startup', label: 'Soft startup' },
  { id: 'direct_repair', label: 'Direct repair' },
  { id: 'space_then_reconnect', label: 'Space then reconnect' },
];

export function MessagePathComposerCard({
  selectedStyle,
  onStyleChange,
  projection,
  script,
  onUsePath,
  onTrack,
}: {
  selectedStyle: CounterfactualStyle;
  onStyleChange: (style: CounterfactualStyle) => void;
  projection: CounterfactualProjection | null;
  script: CounterfactualScript | null;
  onUsePath: () => void;
  onTrack?: (event: string, properties?: Record<string, unknown>) => void;
}) {
  return (
    <Card className="p-5 border-sky-200/80 bg-gradient-to-br from-sky-50 to-white dark:from-sky-950/30 dark:to-neutral-900">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
            <span className="inline-flex items-center gap-1.5">
              Message path preview (24h)
              <InfoHint
                title="Message path preview"
                description="Run style simulations and compare score movement, confidence, and exact scripts before sending anything."
              />
            </span>
          </p>
          <p className="text-sm text-neutral-700 dark:text-neutral-300">
            Live branching engine: choose a communication style, inspect projected movement, then commit.
          </p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {STYLE_OPTIONS.map((style) => (
          <button
            key={style.id}
            type="button"
            onClick={() => {
              onStyleChange(style.id);
              onTrack?.('ri_message_style_selected', { style: style.id });
            }}
            className={`rounded-xl border px-3 py-2 text-left text-sm transition-colors ${
              selectedStyle === style.id
                ? 'border-sky-500 bg-sky-100/80 text-sky-900 dark:border-sky-500 dark:bg-sky-900/30 dark:text-sky-100'
                : 'border-neutral-200 bg-white text-neutral-700 hover:border-sky-300 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200'
            }`}
          >
            {style.label}
          </button>
        ))}
      </div>

      {projection ? (
        <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50/70 px-3 py-2 dark:border-sky-900/50 dark:bg-sky-950/25">
          <p className="text-sm text-neutral-800 dark:text-neutral-100">
            Expected next-day score change:{' '}
            <span className="font-semibold">{projection.delta24h >= 0 ? `-${projection.delta24h}` : `+${Math.abs(projection.delta24h)}`}</span>
            {projection.projectedScore24h != null ? (
              <> · expected score <span className="font-semibold">{projection.projectedScore24h}</span></>
            ) : null}
            {' '}· confidence {Math.round(projection.confidence * 100)}%
          </p>
          <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-300">{projection.rationale}</p>
          {script ? (
            <div className="mt-2 rounded-lg border border-sky-200/80 bg-white/85 px-2.5 py-2 text-xs dark:border-sky-800/50 dark:bg-neutral-900/70">
              <p className="font-semibold text-sky-800 dark:text-sky-200">What to say right now</p>
              <p className="mt-1 text-neutral-800 dark:text-neutral-100">“{script.opener}”</p>
              <p className="mt-1 text-neutral-600 dark:text-neutral-300">Follow-up: “{script.followUp}”</p>
              <p className="mt-1 text-rose-700 dark:text-rose-300">Avoid: “{script.avoidLine}”</p>
            </div>
          ) : null}
          <div className="mt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                onTrack?.('ri_path_committed', { style: selectedStyle });
                onUsePath();
              }}
            >
              <WandSparkles className="mr-1.5 h-3.5 w-3.5" />
              Use this path now
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
